
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, AlertTriangle, Clock } from "lucide-react";
import { toArabicNumbers, formatPercentageWithArabicNumbers } from "@/utils/arabicNumbers";

interface LateAttendanceTopStudentsProps {
  bestAttendanceStudents: any[];
  mostLateStudents: any[];
  studentsWithLateAlerts: any[];
  selectedPeriod: string;
}

export const LateAttendanceTopStudents = ({
  bestAttendanceStudents,
  mostLateStudents,
  studentsWithLateAlerts,
  selectedPeriod
}: LateAttendanceTopStudentsProps) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      {/* Best Attendance Students */}
      <Card className="bg-gradient-to-br from-green-50/90 to-emerald-50/90 backdrop-blur-sm border-green-200/50 shadow-lg">
        <CardHeader className="pb-3 bg-secondary text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-white text-lg">
            <Trophy className="h-4 w-4" />
            الأفضل حضوراً
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 pt-4">
          {bestAttendanceStudents.length > 0 ? (
            bestAttendanceStudents.slice(0, 5).map((student, index) => (
              <div key={student.id} className="flex justify-between items-center p-2 bg-white/60 rounded border border-green-100">
                 <div className="flex items-center gap-2">
                   <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                     {toArabicNumbers(index + 1)}
                   </Badge>
                   <span className="text-sm font-medium text-green-700">{student.name}</span>
                 </div>
                 <div className="flex gap-1 text-xs">
                   <span className="text-green-600 font-medium">{formatPercentageWithArabicNumbers(student.attendancePercentage)}</span>
                   <span className="text-green-500">({toArabicNumbers(student.lateDays || 0)} متأخر)</span>
                 </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-green-600 text-center py-2">لا توجد بيانات</p>
          )}
        </CardContent>
      </Card>

      {/* Most Late Students */}
      <Card className="bg-gradient-to-br from-orange-50/90 to-amber-50/90 backdrop-blur-sm border-orange-200/50 shadow-lg">
        <CardHeader className="pb-3 bg-secondary text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-white text-lg">
            <Clock className="h-4 w-4" />
            الأكثر تأخراً
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 pt-4">
          {mostLateStudents.length > 0 ? (
            mostLateStudents.slice(0, 5).map((student, index) => (
              <div key={student.id} className="flex justify-between items-center p-2 bg-white/60 rounded border border-orange-100">
                 <div className="flex items-center gap-2">
                   <Badge variant="secondary" className="bg-orange-100 text-orange-700 text-xs">
                     {toArabicNumbers(index + 1)}
                   </Badge>
                   <span className="text-sm font-medium text-orange-700">{student.name}</span>
                 </div>
                 <div className="flex gap-1 text-xs">
                   <span className="text-orange-600 font-medium">{toArabicNumbers(student.lateDays || 0)} يوم</span>
                   <span className="text-orange-500">({formatPercentageWithArabicNumbers(student.attendancePercentage)})</span>
                 </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-orange-600 text-center py-2">لا توجد بيانات</p>
          )}
        </CardContent>
      </Card>

      {/* Students with Late Alerts */}
      <Card className="bg-gradient-to-br from-red-50/90 to-rose-50/90 backdrop-blur-sm border-red-200/50 shadow-lg">
        <CardHeader className="pb-3 bg-secondary text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-white text-lg">
            <AlertTriangle className="h-4 w-4" />
            تنبيهات التأخر
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 pt-4">
          {studentsWithLateAlerts.length > 0 ? (
            studentsWithLateAlerts.slice(0, 5).map((student, index) => (
              <div key={student.id} className="flex justify-between items-center p-2 bg-white/60 rounded border border-red-100">
                <div className="flex items-center gap-2">
                  <Badge variant="destructive" className="bg-red-100 text-red-700 text-xs">
                    تنبيه
                  </Badge>
                  <span className="text-sm font-medium text-red-700">{student.name}</span>
                </div>
                 <div className="flex gap-1 text-xs">
                   <span className="text-red-600 font-medium">{toArabicNumbers(student.lateDays || 0)} يوم</span>
                   <span className="text-red-500">متأخر</span>
                 </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-red-600 text-center py-2">لا توجد تنبيهات</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
